import React, { useEffect } from 'react';
import { GetStaticPaths, GetStaticProps } from 'next';
import FunwooAPI, { BuildingShowcaseDetailsEntity } from '@/swagger';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import FullPage from '@/src/components/UI/FullPage';
import OpenGraph from '@/components/OpenGraph';
import * as googleMaps from 'google-maps';
import { useAsync } from 'react-use';
import { Box } from '@chakra-ui/react';
import Image from 'next/image';
import Link from 'next/link';

interface Props {
  showcase: BuildingShowcaseDetailsEntity;
}

const Street: React.FC<Props> = ({
  showcase: { latitude, longitude, adTitle, sid, featureDescription, buildingImage },
}) => {
  const { value: google } = useAsync(
    async () => window.google ?? new googleMaps.Loader(process.env.NEXT_PUBLIC_GOOGLE_MAP_KEY).load(),
    []
  );

  useEffect(() => {
    if (!google) return;

    new google.maps.StreetViewPanorama(document.getElementById('street-view') as HTMLElement, {
      position: { lat: latitude, lng: longitude },
      pov: { heading: 0, pitch: 0 },
      zoom: 1,
    });
  }, [google]);

  return (
    <React.Fragment>
      <OpenGraph
        title={`FUNWOO 德載國際不動產 - ${adTitle}`}
        type='funwoo.building'
        url={`https://funwoo.com.tw/building/${sid}`}
        imageUrl={buildingImage[0]?.watermark ?? buildingImage[0]?.original ?? ''}
        description={featureDescription}
      />
      <FullPage
        title={adTitle}
        hasPadding
        hasBottomMargin={false}
        hasHeaderPlaceHolder
        headerRight={['share']}
        shareSocialInfo={{
          title: adTitle,
          url: `https://funwoo.com.tw/building/${sid}`,
          text: featureDescription,
        }}
        frameProps={{
          className: 'flex flex-col flex-1 w-full',
        }}
        mobileFlexProps={{
          id: 'mobile-content',
          className: 'flex flex-col flex-1 w-full',
        }}
        appContentProps={{
          w: 'full',
          h: 'full',
        }}
      >
        <div className={'relative -mx-4 h-full'}>
          <div id={'street-view'} className={'w-full h-full'}></div>
          <Link passHref href={`/buy/${sid}/map`}>
            <Box as={'a'} position={'absolute'} right={'60px'} zIndex={50} bottom={'18px'} w={'15%'}>
              <Image src={'/map.png'} width={360} height={270} objectFit={'contain'} />
            </Box>
          </Link>
        </div>
      </FullPage>
    </React.Fragment>
  );
};

export default Street;

export const getStaticPaths: GetStaticPaths<{ sid: string }> = async () => {
  const { data: sids } = await FunwooAPI.buildingApi.getBuildingSids();

  return {
    paths: sids.map((sid) => ({
      params: {
        sid,
      },
    })),
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps<Props> = async ({ locale = '', params = { entity: [] } }) => {
  try {
    const sid = params.sid as string;
    const { data } = await FunwooAPI.buildingApi.getShowcaseDetails(sid);
    return {
      props: {
        ...(await serverSideTranslations(locale, ['common'])),
        showcase: data,
      },
      revalidate: Number(process.env.NEXT_PUBLIC_REVALIDATE_INTERVAL),
    };
  } catch (e) {
    return {
      notFound: true,
    };
  }
};
